import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, catchError, map, filter, tap, delay } from 'rxjs/operators';
import { SignUpEffectActions, SignUpFormActions } from './sign-up.actions';
import { SignUpService } from './sign-up.service';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { Router } from '@angular/router';
import { SignUpErrorCode } from './models/sign-up-error-codes';

@Injectable()
export class SignUpEffects {
  protected signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpFormActions.signUpClicked),
      switchMap(({ options }) =>
        this.authService.signUp(options).pipe(
          map(({ Id }) => SignUpEffectActions.signUpSucceeded({ userId: Id })),
          catchError(({ code, description }) =>
            // strip out other properties of error as some are mutable which trigger ngrx store freeze errors
            of(SignUpEffectActions.signUpErrored({ error: { code, description } })),
          ),
        ),
      ),
    ),
  );

  // log password validation errors to Sentry to alert where our
  // client side validators don't catch an error enforced by the server
  protected logPasswordValidationErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SignUpEffectActions.signUpErrored),
        filter(
          ({ error: { code } }) =>
            // password strength errors || password required errors
            code === SignUpErrorCode.InvalidPassword || code.startsWith('String (password)'),
        ),
        tap(({ error: { description } }) => {
          const logError = new Error();
          logError.message = description;
          logError.name = 'SignUpPasswordValidationError';
          Sentry.captureException(logError);
        }),
      ),
    { dispatch: false },
  );

  protected redirectToSignInWhenUserExists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpEffectActions.signUpErrored),
      filter(({ error: { code } }) => code === SignUpErrorCode.UserExists),
      delay(3000),
      tap(() => this.router.navigate(['/'])),
      map(() => SignUpEffectActions.redirectedToSignIn()),
    ),
  );

  protected redirectToVerifyEmailOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SignUpEffectActions.signUpSucceeded),
      tap(({ userId }) => this.router.navigate(['/auth/verify-email', userId])),
      map(() => SignUpEffectActions.redirectedToVerifyEmail()),
    ),
  );

  constructor(
    private actions$: Actions,
    private authService: SignUpService,
    private router: Router,
  ) {}
}
